/**
 * @file PrivacyPolicyPage.tsx
 * @description Public privacy policy at /privacy. Renders sections via
 *   the PolicySection helper which reads bilingual i18n keys. The
 *   policy is versioned by date (privacy_policy.effective_date) so
 *   consent_log rows reference an immutable string.
 * @module Pages
 * @security No user input; safe to render anywhere. The policy text is
 *   MVP-grade and was drafted alongside the code — not a substitute for
 *   a lawyer's review before going commercial.
 */

import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { PolicyLayout, PolicySection } from '../components/common/PolicyLayout';
import { PublicFooter } from '../components/common/PublicFooter';

const SECTION_KEYS = [
  'controller',
  'data_we_collect',
  'how_we_use',
  'lawful_basis',
  'retention',
  'your_rights',
  'cookies',
  'subprocessors',
  'location',
  'security',
  'children',
  'changes',
  'contact',
] as const;

export default function PrivacyPolicyPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <PolicyLayout
        titleKey="privacy_policy.title"
        effectiveDateKey="privacy_policy.effective_date"
        versionKey="privacy_policy.version"
      >
        <p>{t('privacy_policy.intro')}</p>
        {SECTION_KEYS.map((key) => (
          <PolicySection
            key={key}
            titleKey={`privacy_policy.sections.${key}.title`}
            bodyKey={`privacy_policy.sections.${key}.body`}
          />
        ))}
      </PolicyLayout>
      <PublicFooter />
    </>
  );
}
